import { Box, Button, Checkbox, Chip, Divider, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DialogWrapper } from '../../../components/presentation-components/display-elements-components/dialog-wrapper';
import { ShallowFormDisplay } from '../../../components/presentation-components/form-field-components/general/shallow-form-display';
import { SelectableCard } from '../../../components/presentation-components/settings-components/data-import/selectable-card';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { formatEmissionAmount } from '../../../shared/utils/emission-summary-utils';
import CloudEmissionsService from '../shared-services/cloud-emissions/cloud-emissions.service';
import {
  CLOUD_PROVIDER_COPY,
  CloudResolveAction,
  ICloudBreakdownEntry,
  ICloudConnectionStatus,
  ICloudLine,
  ICloudRecordSummary,
  scopeNoun,
} from '../shared-services/cloud-emissions/cloud-emissions.model';
import {
  CLOUD_STATUS_CHIPS,
  describeDecision,
  formatInvoiceAmount,
  formatMonthKey,
  formatRecordDates,
} from './cloud-emissions-format';

const DECIDABLE = ['READY', 'REVIEW', 'LOCKED'];

/**
 * One vendor month: the figure and what sits behind it (basis, scope split, the vendor's
 * breakdown by service and region), what booking it will do, and, for a month that needs a
 * decision, the records it could belong on. Every button says what it does to the footprint.
 */
export const CloudMonthDialog: FunctionComponent<{
  open: boolean,
  line: ICloudLine | null,
  records: Record<string, ICloudRecordSummary>,
  status: ICloudConnectionStatus,
  canAct: boolean,
  onClose: () => void,
  onChanged: () => void,
}> = ({ open, line, records, status, canAct, onClose, onChanged }) => {
  const cloudEmissionsService = new CloudEmissionsService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [excludeOthers, setExcludeOthers] = useState(false);
  const [busy, setBusy] = useState<CloudResolveAction | 'book' | null>(null);

  const match = line?.carbonDetail.match;
  const offeredIds = [...(match?.candidateEmissionIds ?? []), ...(match?.suspectEmissionIds ?? [])]
    .filter((id, index, all) => all.indexOf(id) === index && records[id]);

  useEffect(() => {
    if (!open || !line) return;
    setChosenId(match?.emissionId ?? (offeredIds.length === 1 ? offeredIds[0] : null));
    setExcludeOthers(false);
    setBusy(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line]);

  if (!line) return null;
  const detail = line.carbonDetail;
  const chip = CLOUD_STATUS_CHIPS[line.carbonStatus];
  const decidable = canAct && DECIDABLE.includes(line.carbonStatus);
  const scopeTitle = scopeNoun(status.scopeLabel, { capitalised: true });
  const otherCandidates = offeredIds.filter(id => id !== chosenId && (match?.candidateEmissionIds ?? []).includes(id));

  const fail = (err: any, fallback: string) => userNotificationsContext.openSnackbar({
    open: true,
    message: err?.response?.data?.message || fallback,
    backgroundColor: 'text.primary',
  });

  const done = (message: string) => {
    userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'primary.dark' });
    onChanged();
    onClose();
  };

  const book = () => {
    setBusy('book');
    cloudEmissionsService.commit(status.provider, [line.id])
      .then(result => {
        if (result.committed.length || result.restated.length) {
          done(line.carbonStatus === 'RESTATED' ? 'Restated figure applied' : 'Month booked');
        } else {
          fail({ response: { data: { message: result.skipped[0]?.message } } }, 'This month could not be booked. Please check it and try again.');
          onChanged();
        }
      })
      .catch(err => fail(err, 'This month could not be booked. Please try again.'))
      .finally(() => setBusy(null));
  };

  const resolve = (action: CloudResolveAction) => {
    setBusy(action);
    cloudEmissionsService.resolve(status.provider, line.id, {
      action,
      emissionId: action === 'attach' ? chosenId : null,
      excludeEmissionIds: action === 'attach' && excludeOthers ? otherCandidates : [],
    })
      .then(() => done(action === 'skip' ? 'Month skipped' : 'Month booked'))
      .catch(err => fail(err, 'That month could not be updated. Please try again.'))
      .finally(() => setBusy(null));
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={`${formatMonthKey(detail.monthKey)}: ${detail.scopeName}`}
      cancelHandler={onClose}
      submitHandler={onClose}
      isSubmitting={false}
      hideCancelButton
      submitButtonText='Close'
      maxWidth='md'
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Chip label={chip.label} color={chip.color} variant='outlined' />
          {detail.basisMismatch && <Chip label='Basis differs from your organisation' color='warning' variant='outlined' />}
        </Stack>
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {describeDecision(line, records, status.vendorName)}
        </Typography>

        <Stack>
          <ShallowFormDisplay name={scopeTitle} value={detail.scopeName === detail.scopeId ? detail.scopeId : `${detail.scopeName} (${detail.scopeId})`} isTopRow />
          <ShallowFormDisplay name={`Figure (${detail.basis === 'MARKET' ? 'market-based' : 'location-based'})`} value={formatEmissionAmount(line.total)} />
          {detail.kgOtherBasis != null && detail.otherBasis && (
            <ShallowFormDisplay name={`Other basis (${detail.otherBasis === 'MARKET' ? 'market-based' : 'location-based'})`} value={formatEmissionAmount(detail.kgOtherBasis)} />
          )}
          <ShallowFormDisplay
            name='Vendor scope split'
            value={['Scope 1', 'Scope 2', 'Scope 3']
              .map((label, index) => {
                const kg = [detail.scopeSplit.scope1Kg, detail.scopeSplit.scope2Kg, detail.scopeSplit.scope3Kg][index];
                return `${label}: ${formatEmissionAmount(kg)}`;
              })
              .join(', ')}
          />
          <ShallowFormDisplay name='Reported in your footprint as' value='Scope 3 Category 1, purchased services' />
          <ShallowFormDisplay name='Vendor model' value={line.reference || 'Not stated'} isBottomRow={!detail.basisAssumption} />
          {detail.basisAssumption && <ShallowFormDisplay name='Basis note' value={detail.basisAssumption} isBottomRow />}
        </Stack>

        {detail.restatement && (
          <Stack spacing={0.5}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Restated by the vendor</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Booked at {formatEmissionAmount(detail.restatement.bookedKg)}
              {detail.restatement.history.map(entry => `, then restated to ${formatEmissionAmount(entry.kg)}`).join('')}.
              {detail.restatement.appliedAt ? ' The record now carries the latest figure.' : ' Book the month to update the record.'}
            </Typography>
          </Stack>
        )}

        <Stack spacing={1}>
          <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Where the carbon came from</Typography>
          {detail.breakdown && detail.breakdown.status === 'OK'
            && (detail.breakdown.byService.length > 0 || detail.breakdown.byRegion.length > 0) ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <BreakdownList title={CLOUD_PROVIDER_COPY[status.provider].serviceBreakdownTitle} entries={detail.breakdown.byService} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <BreakdownList title='By region' entries={detail.breakdown.byRegion} />
                </Grid>
              </Grid>
            ) : (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {detail.breakdown?.note || 'Breakdown not available for this month. The monthly figure is unaffected.'}
              </Typography>
            )}
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            The breakdown is detail from the vendor. It never changes the monthly figure. {detail.exclusionsNote}
          </Typography>
        </Stack>

        {match?.emissionId && line.isCommitted && (
          <Button component={Link} to={`/emissions/overview/${match.emissionId}`} variant='outlined' sx={{ alignSelf: 'flex-start' }}>
            <Typography>Open the record</Typography>
          </Button>
        )}

        {canAct && line.carbonStatus === 'RESTATED' && (
          <Stack direction='row' justifyContent='flex-end'>
            <LoadingButton variant='contained' loading={busy === 'book'} onClick={book}>
              <Typography>Update the record to the restated figure</Typography>
            </LoadingButton>
          </Stack>
        )}

        {decidable && (
          <>
            <Divider />
            {offeredIds.length > 0 && (
              <Stack spacing={1}>
                <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>
                  {offeredIds.length === 1 ? 'Invoice record for this month' : 'Choose the invoice record for this month'}
                </Typography>
                <Grid container spacing={2}>
                  {offeredIds.map(id => {
                    const record = records[id];
                    const amount = formatInvoiceAmount(record);
                    const parts = [
                      formatRecordDates(record),
                      amount ? `invoice ${amount}` : null,
                      record.invoice?.reference ? `ref ${record.invoice.reference}` : null,
                      record.nominalCode || record.nominalDescription
                        ? `account ${[record.nominalCode, record.nominalDescription].filter(Boolean).join(' ')}`
                        : null,
                      `${formatEmissionAmount(record.kgCounting)} counting now`,
                      record.locked ? 'period locked' : null,
                    ].filter(Boolean);
                    return (
                      <Grid item xs={12} md={6} key={id}>
                        <SelectableCard
                          align='left'
                          compact
                          title={record.supplierName || 'Unnamed supplier'}
                          description={parts.join(' · ')}
                          selected={chosenId === id}
                          disabled={record.locked}
                          onClick={() => setChosenId(id)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                {otherCandidates.length > 0 && chosenId && (
                  <FormControlLabel
                    control={<Checkbox checked={excludeOthers} onChange={e => setExcludeOthers(e.target.checked)} />}
                    label={<Typography variant='body2'>Exclude the other {otherCandidates.length === 1 ? 'record' : 'records'} from reporting, because this figure covers the same usage</Typography>}
                  />
                )}
              </Stack>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='flex-end' spacing={2}>
              <Button variant='text' disabled={!!busy} onClick={() => resolve('skip')}>
                <Typography>Skip this month</Typography>
              </Button>
              <LoadingButton variant='outlined' loading={busy === 'standalone'} disabled={!!busy && busy !== 'standalone'} onClick={() => resolve('standalone')}>
                <Typography>Book as its own record</Typography>
              </LoadingButton>
              {offeredIds.length > 0 && (
                <LoadingButton variant='contained' loading={busy === 'attach'} disabled={!chosenId || (!!busy && busy !== 'attach')} onClick={() => resolve('attach')}>
                  <Typography>Use the vendor figure and stop counting the invoice estimate</Typography>
                </LoadingButton>
              )}
            </Stack>
            {offeredIds.length > 0 && (
              <Typography variant='caption' sx={{ color: 'text.secondary', textAlign: 'right' }}>
                Booking as its own record keeps the invoice estimate counting too. Only do that if none of these
                records covers the same usage, or this month will be counted twice.
              </Typography>
            )}
          </>
        )}
      </Stack>
    </DialogWrapper>
  );
};

const BreakdownList: FunctionComponent<{ title: string, entries: ICloudBreakdownEntry[] }> = ({ title, entries }) => {
  const total = entries.reduce((sum, entry) => sum + entry.kg, 0);
  return (
    <Stack spacing={0.5}>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>{title}</Typography>
      {entries.length === 0 && <Typography variant='body2' sx={{ color: 'text.secondary' }}>Not available</Typography>}
      {entries.slice(0, 12).map(entry => (
        <Stack key={entry.name} direction='row' justifyContent='space-between' spacing={2}
          sx={{ borderBottom: '1px solid', borderColor: 'lightGrey.main', py: 0.5 }}>
          <Typography variant='body2' sx={{ color: 'text.primary', wordBreak: 'break-word' }}>{entry.name}</Typography>
          <Box sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
              {formatEmissionAmount(entry.kg)}{total > 0 ? ` (${Math.round((entry.kg / total) * 100)}%)` : ''}
            </Typography>
          </Box>
        </Stack>
      ))}
      {entries.length > 12 && (
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          And {entries.length - 12} more, together {formatEmissionAmount(entries.slice(12).reduce((sum, entry) => sum + entry.kg, 0))}.
        </Typography>
      )}
    </Stack>
  );
};
